import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import { useCurrentApplication } from 'application/hooks/useCurrentApplication';
import { useApplicationUsageLimits } from 'application/hooks/useApplicationUsageLimits';
import { useApplicationUsageStatistics } from 'application/hooks/useApplicationUsageStatistics';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useUsageLimits } from 'organizations/hooks/useUsageLimits';

import { relativeTimeFromNow } from 'services/date';

import { ExternalLink } from 'components/ExternalLink/ExternalLink';
import { Heading } from 'components/Heading/Heading';
import { Loader } from 'components/Loader/Loader';
import { Text } from 'components/Text/Text';
import * as PageLayout from 'components/PageLayout';
import {
  WorkspaceCloudStorageUtilizationCard,
  WorkspaceDeploymentsUtilizationCard,
  WorkspaceRecordedAnalyticsUtilizationCard,
  WorkspaceRetentionUtilizationCard,
  WorkspaceStreamsUtilizationCard,
} from 'application/components/WorkspaceUtilizationCards/WorkspaceUtilizationCards';

export function WorkspaceUtilization() {
  const { data: application } = useCurrentApplication();
  const { data: currentPlan } = useCurrentPlan();
  const { data: usageStatistics, isLoading } = useApplicationUsageStatistics();
  const { isLoading: isLoadingLimits } = useApplicationUsageLimits();
  const { isLoading: isLoadingOrganizationLimits } = useUsageLimits();

  if (isLoading || isLoadingLimits || isLoadingOrganizationLimits) {
    return <Loader size="small" text="Loading utilization data..." />;
  }

  if (!usageStatistics || !application || currentPlan === undefined) {
    return (
      <Text align="center">
        Utilization data currently unavailable.
        <br />
        Please try again later.
      </Text>
    );
  }

  return (
    <PageLayout.Root size="medium">
      <PageLayout.Container>
        <div className="grid gap-4 py-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <Heading level="2">Workspace utilization</Heading>
              <ExternalLink href="https://docs.lumeo.com/docs/utilization">
                Utilization docs
              </ExternalLink>
              <Text>
                {application.name} · limits set by your organization admin
              </Text>
            </div>

            <Text size="small" className="my-0 flex-none">
              Updated {relativeTimeFromNow(usageStatistics.updated_at)}
            </Text>
          </div>

          <section>
            <VisuallyHidden>
              <h2>Utilization</h2>
            </VisuallyHidden>

            <ul className="grid list-none grid-cols-1 gap-3.5 p-0">
              <li className="min-w-0">
                <WorkspaceStreamsUtilizationCard />
              </li>

              <li className="min-w-0">
                <WorkspaceRecordedAnalyticsUtilizationCard />
              </li>

              {currentPlan?.can_retain_recordings && (
                <li className="min-w-0">
                  <WorkspaceRetentionUtilizationCard />
                </li>
              )}

              <li className="min-w-0">
                <WorkspaceCloudStorageUtilizationCard />
              </li>

              <li className="min-w-0">
                <WorkspaceDeploymentsUtilizationCard />
              </li>
            </ul>
          </section>
        </div>
      </PageLayout.Container>
    </PageLayout.Root>
  );
}
